import { useId, useState } from "react";
import { ApiError } from "../api/client";
import type { EventType } from "../api/types";
import { DateTimeField } from "./DateTimeField";
import {
  useCreateManualAdjustment,
  useCreateWorkerEvent,
  useDirectCorrectTimeEvent,
} from "../hooks/useTimeEvents";

const EVENT_TYPE_LABELS_FR: Record<EventType, string> = {
  attendance_in: "Pointage entrée",
  attendance_out: "Pointage sortie",
  activity_start: "Début activité",
  activity_end: "Fin activité",
  break_start: "Début pause",
  break_end: "Fin pause",
  location_ping: "Position GPS",
  manual_adjustment: "Ajustement manuel",
};

// 409 = feuille déjà soumise (assertTimesheetDraft) : la rouvrir avant d'éditer.
const ERROR_BY_STATUS: Record<number, string> = {
  403: "Ce collaborateur n'est pas dans votre équipe.",
  409: "Feuille de temps déjà soumise : rouvrez-la depuis la revue pour corriger ce pointage.",
  422: "Heure invalide pour ce pointage (une sortie doit suivre l'entrée).",
};

function errorMessage(e: unknown): string {
  const status = e instanceof ApiError ? e.status : 0;
  return (
    ERROR_BY_STATUS[status] ??
    "Erreur lors de l'enregistrement. Veuillez réessayer."
  );
}

const CREATABLE_EVENT_TYPES: EventType[] = [
  "attendance_in",
  "attendance_out",
  "activity_start",
  "activity_end",
  "break_start",
  "break_end",
  "manual_adjustment",
];

type CorrectMode = {
  mode: "correct";
  eventId: string;
  defaultOccurredAt: string;
  reasonRequired?: boolean;
  // Type de l'event créé par la correction. "attendance_out" ferme une session
  // ouverte (résolution anomalie). Omis = manual_adjustment (édition d'un fait).
  correctedEventType?: "attendance_out";
  // Libellé du champ date/heure (ex. "Heure de sortie réelle" pour une anomalie).
  dateLabel?: string;
};

type CreateMode = {
  mode: "create";
  defaultOccurredAt: string;
  defaultEventType?: EventType;
  // self=true : création worker self-service (POST /workers/timeevents),
  // worker_url forcé côté serveur. Sinon création manager (workerUrl requis).
  self?: boolean;
  workerUrl?: string;
};

type Props = (CorrectMode | CreateMode) & {
  ctaLabel?: string;
  ctaVariant?: "primary" | "secondary";
  onDone?: () => void;
};

/**
 * EventEditForm : Manager/admin only. Two modes:
 *  - `correct` : remplace un event existant (workflow direct, hors anomalie)
 *  - `create`  : crée un manual_adjustment pour combler un trou
 *
 * Réutilisé par AnomaliesScreen et TimesheetScreen.
 */
export function EventEditForm(props: Props) {
  const { ctaLabel, ctaVariant = "primary", onDone } = props;
  const [open, setOpen] = useState(false);
  const [proposedTime, setProposedTime] = useState("");
  const [reason, setReason] = useState("");
  const fieldId = useId();
  const [eventType, setEventType] = useState<EventType>(
    props.mode === "create"
      ? (props.defaultEventType ?? "attendance_in")
      : "manual_adjustment",
  );

  const correctMutation = useDirectCorrectTimeEvent();
  const createManagerMutation = useCreateManualAdjustment();
  const createWorkerMutation = useCreateWorkerEvent();
  const isSelfCreate = props.mode === "create" && props.self === true;
  const createMutation = isSelfCreate
    ? createWorkerMutation
    : createManagerMutation;
  const mutation = props.mode === "correct" ? correctMutation : createMutation;

  const reasonRequired =
    props.mode === "create" ||
    (props.mode === "correct" && props.reasonRequired);

  function handleOpen() {
    setOpen(true);
    setProposedTime(props.defaultOccurredAt);
  }

  function handleCancel() {
    setOpen(false);
    setReason("");
    setProposedTime("");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!proposedTime) return;
    if (reasonRequired && !reason.trim()) return;
    const occurredAt = new Date(proposedTime).toISOString();

    if (props.mode === "correct") {
      correctMutation.mutate(
        {
          eventId: props.eventId,
          occurredAt,
          reason: reason.trim() || undefined,
          eventType: props.correctedEventType,
        },
        {
          onSuccess: () => {
            handleCancel();
            onDone?.();
          },
        },
      );
      return;
    }

    const onSuccess = () => {
      handleCancel();
      onDone?.();
    };

    if (isSelfCreate) {
      createWorkerMutation.mutate(
        { eventType, occurredAt, reason: reason.trim() },
        { onSuccess },
      );
      return;
    }

    createManagerMutation.mutate(
      {
        workerUrl: props.workerUrl ?? "",
        eventType,
        occurredAt,
        reason: reason.trim(),
      },
      { onSuccess },
    );
  }

  const buttonLabel =
    ctaLabel ?? (props.mode === "create" ? "Ajouter un événement" : "Modifier");

  if (!open) {
    return (
      <button
        className={`pill-btn ${ctaVariant === "secondary" ? "secondary" : ""}`}
        onClick={handleOpen}
      >
        {buttonLabel}
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 8 }}>
      {props.mode === "create" && (
        <div style={{ marginBottom: 8 }}>
          <label
            htmlFor={`${fieldId}-type`}
            className="sub"
            style={{ display: "block", marginBottom: 4, fontSize: 12 }}
          >
            Type d'événement
          </label>
          <select
            id={`${fieldId}-type`}
            className="field"
            value={eventType}
            onChange={(e) => setEventType(e.target.value as EventType)}
          >
            {CREATABLE_EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {EVENT_TYPE_LABELS_FR[t]}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <label
          htmlFor={`${fieldId}-time`}
          className="sub"
          style={{ display: "block", marginBottom: 4, fontSize: 12 }}
        >
          {props.mode === "correct" && props.dateLabel
            ? props.dateLabel
            : "Date et heure"}
        </label>
        <DateTimeField
          id={`${fieldId}-time`}
          value={proposedTime}
          onChange={setProposedTime}
          required
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <label
          htmlFor={`${fieldId}-reason`}
          className="sub"
          style={{ display: "block", marginBottom: 4, fontSize: 12 }}
        >
          Justification{reasonRequired ? "" : " (optionnelle)"}
        </label>
        <textarea
          id={`${fieldId}-reason`}
          className="field field--textarea"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            reasonRequired
              ? "Raison obligatoire"
              : "Optionnel, rempli automatiquement sinon"
          }
          required={reasonRequired}
          rows={2}
        />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="submit"
          className="pill-btn"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Envoi…" : "Confirmer"}
        </button>
        <button
          type="button"
          className="pill-btn secondary"
          onClick={handleCancel}
        >
          Annuler
        </button>
      </div>

      {mutation.isError && (
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--danger-fg)" }}>
          {errorMessage(mutation.error)}
        </div>
      )}
    </form>
  );
}
